import styles from './ReadingPaneContainer.scss';
import { READING_PANE_CONTAINER_ID, READING_PANE_MIN_VISIBLE_HEIGHT } from '../constants';
import { observer } from 'mobx-react-lite';
import { composeStore } from 'owa-mail-compose-store';
import { FullCompose } from 'owa-mail-compose-view';
import { messageAdListViewStatusStore } from 'owa-mail-messagead-list-store';
import { MessageAdReadingPane } from 'owa-mail-messagead-readingpane-view';
import { MultiSelectReadingPane } from 'owa-mail-multiselect-reading-pane-view';
import { ReadingPane } from 'owa-mail-reading-pane-view';
import { useBooleanState } from 'owa-react-hooks/lib/useBooleanState';
import { useCustomTimeout } from 'owa-react-hooks/lib/useCustomTimeout';
import { lazySubscribeToResizeEvent, lazyUnsubscribeFromResizeEvent } from 'owa-resize-event';
import { getActiveContentTab, TabType, TabViewState } from 'owa-tab-store';
import * as trace from 'owa-trace';
import * as React from 'react';

export interface ReadingPaneContainerProps {
    isDumpsterOrDumpsterSearchTable?: boolean;
    suppressServerMarkReadOnReplyOrForward?: boolean;
    isNotesFolder?: boolean;
    isItemAttachment?: boolean;
    useMultiSelectReadingPane?: boolean;
}

export default observer(function ReadingPaneContainer(props: ReadingPaneContainerProps) {
    const [
        maximizeScrollRegion,
        setMaximizeScrollRegionRequired,
        setMaximizeScrollRegionNotRequired,
    ] = useBooleanState(false);

    /**
     * This function is used to determine if the reading pane has
     * enough height for reading an email properly. If it does not,
     * the maximizeScrollRegion flag is set to allow scrolling the
     * email subject and avoid the sticky item header, hence there
     * will be more space to read(the scroll region is maximized)
     */
    const checkMaximizeScrollRegionRequired = () => {
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2532 (43,9): Object is possibly 'undefined'.
        // @ts-expect-error
        document.getElementById(READING_PANE_CONTAINER_ID)?.clientHeight <
        READING_PANE_MIN_VISIBLE_HEIGHT
            ? setMaximizeScrollRegionRequired()
            : setMaximizeScrollRegionNotRequired();
    };

    const [setResizeTimer] = useCustomTimeout();

    const onResize = () =>
        setResizeTimer(() => {
            checkMaximizeScrollRegionRequired();
        }, 1);

    React.useEffect(() => {
        // Always check when rendering the RP component in case
        // zoom has been set before resizing
        checkMaximizeScrollRegionRequired();
        const resizeEventScope = ''; //default
        const subscriptionPromise = lazySubscribeToResizeEvent
            .import()
            .then(subscribeToResizeEvent => {
                subscribeToResizeEvent(onResize, resizeEventScope, window);
            });

        return () => {
            subscriptionPromise.then(() => {
                lazyUnsubscribeFromResizeEvent.importAndExecute(onResize, resizeEventScope);
            });
        };
    }, []);

    const renderReadingPane = (): JSX.Element => {
        let renderContent = null;
        const activeTab = getActiveContentTab();

        if (
            activeTab &&
            (activeTab.type == TabType.MailCompose ||
                activeTab.type == TabType.SecondaryReadingPane)
        ) {
            // For secondary tab, it is not linked to list view, so always ignore what is selected from list view
            // and just render tab content for secondary tabs
            renderContent = renderTabContent(activeTab);
        }
        // If in dumpster or dumpster search, should always show reading pane (no compose)
        else if (props.isDumpsterOrDumpsterSearchTable && !props.useMultiSelectReadingPane) {
            renderContent = (
                <ReadingPane
                    isDumpsterOrDumpsterSearchTable={props.isDumpsterOrDumpsterSearchTable}
                    isFromMainWindow={true}
                    isItemAttachment={props.isItemAttachment}
                    maximizeScrollRegion={maximizeScrollRegion}
                />
            );
        } else if (props.useMultiSelectReadingPane) {
            renderContent = (
                <MultiSelectReadingPane
                    isDumpsterOrDumpsterSearchTable={props.isDumpsterOrDumpsterSearchTable}
                    isNotesFolder={props.isNotesFolder}
                />
            );
        } else {
            const primaryComposeViewState =
                composeStore.primaryComposeId &&
                composeStore.viewStates.get(composeStore.primaryComposeId);
            // Priority for content to be loaded into the Reading Pane:
            // 1. If a message ad has been clicked.
            // 2. If we're in tab view and have an activate tab matching these types.
            // 3. If a full compose is open.
            // 4. Render the base reading pane out of the store.
            if (messageAdListViewStatusStore.selectedAdId) {
                renderContent = (
                    <MessageAdReadingPane
                        selectedAdRowId={messageAdListViewStatusStore.selectedAdId}
                    />
                );
            } else if (primaryComposeViewState && !primaryComposeViewState.isInlineCompose) {
                renderContent = (
                    <FullCompose
                        viewState={primaryComposeViewState}
                        key={primaryComposeViewState.composeId}
                        isFromMainWindow={true}
                    />
                );
            } else {
                renderContent = (
                    <ReadingPane
                        isDumpsterOrDumpsterSearchTable={props.isDumpsterOrDumpsterSearchTable}
                        isFromMainWindow={true}
                        isItemAttachment={props.isItemAttachment}
                        maximizeScrollRegion={maximizeScrollRegion}
                    />
                );
            }
        }
        return <div key="renderReadingPaneDiv">{renderContent}</div>;
    };
    const renderTabContent = (tab: TabViewState) => {
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (142,13): Type 'null' is not assignable to type 'Element'.
        // @ts-expect-error
        let content: JSX.Element = null;
        switch (tab.type) {
            case TabType.MailCompose:
                const composeViewState = composeStore.viewStates.get(tab.data);
                if (composeViewState) {
                    content = (
                        <FullCompose
                            key={composeViewState.composeId}
                            viewState={composeViewState}
                            isFromMainWindow={true}
                        />
                    );
                } else {
                    // for some reason, we are trying to render a tab, but we weren't able to grab any data about that tab.
                    // Tracing here with some info that might help us diagnose.
                    let keyList = '';
                    composeStore.viewStates.forEach((value, key) => {
                        keyList = keyList + key + ' : ';
                    });
                    trace.errorThatWillCauseAlert(
                        '[ReadingPaneContainer] Attempted to render compose tab ID ' +
                            tab.data +
                            ' but it was not present in the compose store. Compose store IDs are ' +
                            keyList
                    );
                }
                break;
            case TabType.SecondaryReadingPane:
                content = (
                    <ReadingPane
                        isDumpsterOrDumpsterSearchTable={props.isDumpsterOrDumpsterSearchTable}
                        isFromMainWindow={true}
                        isItemAttachment={props.isItemAttachment}
                        maximizeScrollRegion={maximizeScrollRegion}
                    />
                );
                break;
        }
        return content;
    };
    return (
        <div
            className={styles.container}
            key="readingPaneContainerDiv"
            id={READING_PANE_CONTAINER_ID}>
            {renderReadingPane()}
        </div>
    );
});
